import { linsolve } from './linsolve';
import {
  DEFAULT_FTOL,
  DEFAULT_MAX_ITER,
  DEFAULT_XTOL,
  type ScalarSolverOptions,
  type SolverResult,
} from './types';

export interface NewtonScalarOptions extends ScalarSolverOptions {
  /** Analitik hosila; berilmasa markaziy sonli farq ishlatiladi */
  df?: (x: number) => number;
  /** Orqaga qadam (backtracking): qoldiq kamaymasa qadam yarimlanadi */
  damping?: boolean;
  /** x uchun ruxsat etilgan oraliq; qadam undan chiqsa chegaraga tortiladi */
  bounds?: [number, number];
}

/** Markaziy sonli hosila; qadam |x| ga nisbiy */
function numericDerivative(f: (x: number) => number, x: number): number {
  const h = 1e-6 * Math.max(1, Math.abs(x));
  return (f(x + h) - f(x - h)) / (2 * h);
}

/**
 * Newton-Raphson, skalyar. Kvadratik konvergensiya, lekin yomon boshlang'ich
 * qiymatda uzoqlashishi mumkin — shuning uchun damping va bounds bor.
 */
export function newtonScalar(
  f: (x: number) => number,
  x0: number,
  opts: NewtonScalarOptions = {},
): SolverResult {
  const xtol = opts.xtol ?? DEFAULT_XTOL;
  const ftol = opts.ftol ?? DEFAULT_FTOL;
  const maxIter = opts.maxIter ?? DEFAULT_MAX_ITER;
  const damping = opts.damping ?? true;
  const df = opts.df ?? ((x: number) => numericDerivative(f, x));

  let x = x0;
  let fx = f(x);
  if (!Number.isFinite(fx)) {
    return {
      converged: false,
      value: x,
      iterations: 0,
      residual: NaN,
      message: 'solver.nonFinite',
    };
  }

  for (let i = 0; i < maxIter; i++) {
    if (Math.abs(fx) <= ftol)
      return { converged: true, value: x, iterations: i, residual: Math.abs(fx) };

    const d = df(x);
    if (!Number.isFinite(d) || d === 0) {
      return {
        converged: false,
        value: x,
        iterations: i,
        residual: Math.abs(fx),
        message: 'solver.singularJacobian',
      };
    }

    let step = -fx / d;
    let xNew = clamp(x + step, opts.bounds);
    let fNew = f(xNew);

    if (damping) {
      let k = 0;
      while ((!Number.isFinite(fNew) || Math.abs(fNew) > Math.abs(fx)) && k < 12) {
        step *= 0.5;
        xNew = clamp(x + step, opts.bounds);
        fNew = f(xNew);
        k++;
      }
      if (!Number.isFinite(fNew)) {
        return {
          converged: false,
          value: x,
          iterations: i,
          residual: Math.abs(fx),
          message: 'solver.nonFinite',
        };
      }
    } else if (!Number.isFinite(fNew)) {
      return {
        converged: false,
        value: xNew,
        iterations: i,
        residual: NaN,
        message: 'solver.nonFinite',
      };
    }

    const moved = Math.abs(xNew - x);
    x = xNew;
    fx = fNew;

    if (moved <= xtol * Math.max(1, Math.abs(x))) {
      if (Math.abs(fx) <= Math.max(ftol, 1e-8 * Math.max(1, Math.abs(fx)))) {
        return { converged: true, value: x, iterations: i + 1, residual: Math.abs(fx) };
      }
      return {
        converged: false,
        value: x,
        iterations: i + 1,
        residual: Math.abs(fx),
        message: 'solver.stalled',
      };
    }
  }
  const ok = Math.abs(fx) <= ftol;
  return ok
    ? { converged: true, value: x, iterations: maxIter, residual: Math.abs(fx) }
    : {
        converged: false,
        value: x,
        iterations: maxIter,
        residual: Math.abs(fx),
        message: 'solver.maxIterations',
      };
}

function clamp(x: number, bounds?: [number, number]): number {
  if (!bounds) return x;
  return Math.min(bounds[1], Math.max(bounds[0], x));
}

export type VectorFn = (x: readonly number[]) => number[];

export interface NewtonSystemOptions {
  xtol?: number;
  ftol?: number;
  maxIter?: number;
  /** Analitik Yakobian J[i][j] = ∂F_i/∂x_j; berilmasa oldinga sonli farq */
  jacobian?: (x: readonly number[]) => number[][];
  damping?: boolean;
}

function numericJacobian(F: VectorFn, x: readonly number[], Fx: readonly number[]): number[][] {
  const n = x.length;
  const J: number[][] = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  for (let j = 0; j < n; j++) {
    const xj = x[j] as number;
    const h = 1e-7 * Math.max(1, Math.abs(xj));
    const xp = [...x];
    xp[j] = xj + h;
    const Fp = F(xp);
    for (let i = 0; i < n; i++) {
      (J[i] as number[])[j] = ((Fp[i] as number) - (Fx[i] as number)) / h;
    }
  }
  return J;
}

function normInf(v: readonly number[]): number {
  let m = 0;
  for (const a of v) {
    const t = Math.abs(a);
    if (!Number.isFinite(t)) return NaN;
    if (t > m) m = t;
  }
  return m;
}

/**
 * Newton-Raphson, ko'p o'zgaruvchili: F(x) = 0.
 * Har iteratsiyada J·Δx = −F yechiladi (Gauss), keyin damping.
 */
export function newtonSystem(
  F: VectorFn,
  x0: readonly number[],
  opts: NewtonSystemOptions = {},
): SolverResult<number[]> {
  const xtol = opts.xtol ?? DEFAULT_XTOL;
  const ftol = opts.ftol ?? DEFAULT_FTOL;
  const maxIter = opts.maxIter ?? DEFAULT_MAX_ITER;
  const damping = opts.damping ?? true;

  let x = [...x0];
  let Fx = F(x);
  let res = normInf(Fx);
  if (!Number.isFinite(res)) {
    return {
      converged: false,
      value: x,
      iterations: 0,
      residual: NaN,
      message: 'solver.nonFinite',
    };
  }

  for (let i = 0; i < maxIter; i++) {
    if (res <= ftol) return { converged: true, value: x, iterations: i, residual: res };

    const J = opts.jacobian ? opts.jacobian(x) : numericJacobian(F, x, Fx);
    const dx = linsolve(
      J,
      Fx.map((v) => -v),
    );
    if (!dx) {
      return {
        converged: false,
        value: x,
        iterations: i,
        residual: res,
        message: 'solver.singularJacobian',
      };
    }

    let lambda = 1;
    let xNew = x.map((v, k) => v + lambda * (dx[k] as number));
    let FNew = F(xNew);
    let resNew = normInf(FNew);

    if (damping) {
      let k = 0;
      while ((!Number.isFinite(resNew) || resNew > res) && k < 12) {
        lambda *= 0.5;
        xNew = x.map((v, idx) => v + lambda * (dx[idx] as number));
        FNew = F(xNew);
        resNew = normInf(FNew);
        k++;
      }
    }
    if (!Number.isFinite(resNew)) {
      return {
        converged: false,
        value: x,
        iterations: i,
        residual: res,
        message: 'solver.nonFinite',
      };
    }

    const moved = normInf(xNew.map((v, k) => v - (x[k] as number)));
    const scale = Math.max(1, normInf(xNew));
    x = xNew;
    Fx = FNew;
    res = resNew;

    if (moved <= xtol * scale) {
      if (res <= Math.max(ftol, 1e-8))
        return { converged: true, value: x, iterations: i + 1, residual: res };
      return {
        converged: false,
        value: x,
        iterations: i + 1,
        residual: res,
        message: 'solver.stalled',
      };
    }
  }
  return res <= ftol
    ? { converged: true, value: x, iterations: maxIter, residual: res }
    : {
        converged: false,
        value: x,
        iterations: maxIter,
        residual: res,
        message: 'solver.maxIterations',
      };
}
